import { z } from "zod";

export const COURSE_TYPES = ["INTERNSHIP", "WORKSHOP", "COURSE", "OTHER"] as const;
export const SESSION_TYPES = ["LIVE", "RECORDED", "OFFLINE"] as const;
export const ATTENDANCE_STATES = ["NOT_MARKED", "PRESENT", "ABSENT", "EXCUSED"] as const;
export const ENROLLMENT_MODES = ["PAID", "FREE", "INVITATION"] as const;

const identifier = z.string().trim().regex(/^[a-zA-Z0-9_-]+$/, "Use letters, numbers, dashes, or underscores.").max(80);
const optionalUrl = z.string().trim().url().max(2000).refine((value) => value.startsWith("https://"), "Use a secure HTTPS URL.").optional().or(z.literal(""));
const dateTime = z.string().datetime({ offset: true });

export const courseAssignmentSchema = z.object({
    id: identifier,
    title: z.string().trim().max(200),
    description: z.string().trim().max(2000).optional().default(""),
    required: z.boolean().optional().default(true),
}).strict();

export const courseSessionSchema = z.object({
    id: identifier,
    title: z.string().trim().max(200),
    type: z.enum(SESSION_TYPES),
    startsAt: dateTime,
    endsAt: dateTime,
    joinUrl: optionalUrl,
    recordingUrl: optionalUrl,
    venue: z.string().trim().max(500).optional().default(""),
    required: z.boolean().optional().default(true),
    cancelled: z.boolean().optional().default(false),
    assignments: z.array(courseAssignmentSchema).max(50).default([]),
}).strict().superRefine((session, context) => {
    if (new Date(session.endsAt).getTime() <= new Date(session.startsAt).getTime()) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["endsAt"], message: "Session end must be after its start." });
    }
});

export const courseModuleSchema = z.object({
    id: identifier,
    title: z.string().trim().max(200),
    label: z.string().trim().max(100).optional().default(""),
    required: z.boolean().optional().default(true),
    sessions: z.array(courseSessionSchema).max(100),
}).strict();

export const courseRunInputSchema = z.object({
    schemaVersion: z.literal(1).default(1),
    programId: identifier.optional(),
    courseTitle: z.string().trim().max(200),
    courseType: z.enum(COURSE_TYPES),
    programTrackKey: identifier.optional().or(z.literal("")),
    programTrackLabel: z.string().trim().max(100).optional().default(""),
    programTier: z.number().int().min(1).max(99).optional(),
    description: z.string().trim().max(4000).default(""),
    learningOutcomes: z.array(z.string().trim().max(300)).max(20).default([]),
    imageUrl: optionalUrl,
    coverImageUrl: optionalUrl,
    cardImageUrl: optionalUrl,
    runTitle: z.string().trim().max(200),
    startAt: dateTime,
    endAt: dateTime,
    timezone: z.string().trim().min(1).max(100).default("Asia/Kolkata"),
    minimumAttendancePercent: z.number().min(0).max(100),
    enrollmentMode: z.enum(ENROLLMENT_MODES).default("INVITATION"),
    priceINR: z.number().positive().max(10000000).optional(),
    capacity: z.number().int().positive().max(100000).optional(),
    publicationStatus: z.enum(["draft", "published"]).default("draft"),
    modules: z.array(courseModuleSchema).max(100),
}).strict().superRefine((course, context) => {
    if (new Date(course.endAt).getTime() <= new Date(course.startAt).getTime()) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["endAt"], message: "Course end must be after its start." });
    }
    if (course.enrollmentMode === "PAID" && !course.priceINR) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["priceINR"], message: "Paid courses require a positive price." });
    }
    if (course.publicationStatus === "published") {
        if (!course.courseTitle) context.addIssue({ code: z.ZodIssueCode.custom, path: ["courseTitle"], message: "Enter a course title." });
        if (!course.runTitle) context.addIssue({ code: z.ZodIssueCode.custom, path: ["runTitle"], message: "Enter a run title." });
        if (!course.description) context.addIssue({ code: z.ZodIssueCode.custom, path: ["description"], message: "Published courses require a description." });
        if (!course.learningOutcomes.some(Boolean)) context.addIssue({ code: z.ZodIssueCode.custom, path: ["learningOutcomes"], message: "Published courses require at least one learning outcome." });
        if (!course.modules.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ["modules"], message: "Published courses require at least one module." });
        if (["WORKSHOP", "INTERNSHIP"].includes(course.courseType) && !course.coverImageUrl) context.addIssue({ code: z.ZodIssueCode.custom, path: ["coverImageUrl"], message: "Published workshops and internships require a cover image." });
        if (["WORKSHOP", "INTERNSHIP"].includes(course.courseType) && !course.cardImageUrl) context.addIssue({ code: z.ZodIssueCode.custom, path: ["cardImageUrl"], message: "Published workshops and internships require a square card image." });
        if (course.courseType !== "OTHER") {
            if (!course.programTrackKey) context.addIssue({ code: z.ZodIssueCode.custom, path: ["programTrackKey"], message: "Published programs require a track key." });
            if (!course.programTrackLabel) context.addIssue({ code: z.ZodIssueCode.custom, path: ["programTrackLabel"], message: "Published programs require a track name." });
            if (!course.programTier) context.addIssue({ code: z.ZodIssueCode.custom, path: ["programTier"], message: "Published programs require a tier." });
        }
        course.modules.forEach((module, moduleIndex) => {
            if (!module.title) context.addIssue({ code: z.ZodIssueCode.custom, path: ["modules", moduleIndex, "title"], message: "Enter a module title." });
            if (!module.sessions.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ["modules", moduleIndex, "sessions"], message: "Add at least one session." });
            module.sessions.forEach((session, sessionIndex) => {
                if (!session.title) context.addIssue({ code: z.ZodIssueCode.custom, path: ["modules", moduleIndex, "sessions", sessionIndex, "title"], message: "Enter a session title." });
                if (session.type === "OFFLINE" && !session.venue) context.addIssue({ code: z.ZodIssueCode.custom, path: ["modules", moduleIndex, "sessions", sessionIndex, "venue"], message: "Offline sessions require a venue." });
                session.assignments.forEach((assignment, assignmentIndex) => {
                    if (!assignment.title) context.addIssue({ code: z.ZodIssueCode.custom, path: ["modules", moduleIndex, "sessions", sessionIndex, "assignments", assignmentIndex, "title"], message: "Enter an assignment title." });
                });
            });
        });
    }
    const ids = new Set<string>();
    course.modules.forEach((module, moduleIndex) => {
        [module.id, ...module.sessions.flatMap((session) => [session.id, ...session.assignments.map((assignment) => assignment.id)])].forEach((id) => {
            if (ids.has(id)) context.addIssue({ code: z.ZodIssueCode.custom, path: ["modules", moduleIndex], message: `Duplicate curriculum id: ${id}` });
            ids.add(id);
        });
        module.sessions.forEach((session, sessionIndex) => {
            const starts = new Date(session.startsAt).getTime();
            const ends = new Date(session.endsAt).getTime();
            if (starts < new Date(course.startAt).getTime() || ends > new Date(course.endAt).getTime()) {
                context.addIssue({ code: z.ZodIssueCode.custom, path: ["modules", moduleIndex, "sessions", sessionIndex], message: "Session must fall within the course dates." });
            }
        });
    });
});

export type CourseAssignment = z.infer<typeof courseAssignmentSchema>;
export type CourseSession = z.infer<typeof courseSessionSchema>;
export type CourseModule = z.infer<typeof courseModuleSchema>;
export type CourseRunInput = z.infer<typeof courseRunInputSchema>;
export type AttendanceState = typeof ATTENDANCE_STATES[number];
export type CourseLifecycle = "draft" | "scheduled" | "active" | "closing" | "completed" | "cancelled";

export type CourseRun = CourseRunInput & {
    id: string;
    programId: string;
    courseStatus: "draft" | "published" | "completed" | "cancelled";
    completedAt?: string | null;
};

export type CourseSubmission = {
    assignmentId: string;
    moduleId: string;
    sessionId: string;
    url: string;
    submittedAt: string;
    updatedAt: string;
};

export type CourseAttendance = {
    sessionId: string;
    status: AttendanceState;
    updatedAt?: string | null;
    updatedByName?: string;
};

export function parseCourseRunJson(value: string): CourseRunInput {
    if (value.length > 700000) throw new Error("Course curriculum is too large. Split it into a smaller course run.");
    let parsed: unknown;
    try { parsed = JSON.parse(value); }
    catch { throw new Error("Curriculum must be valid JSON."); }
    const result = courseRunInputSchema.safeParse(parsed);
    if (!result.success) {
        const issue = result.error.issues[0];
        throw new Error(`${issue.path.join(".") || "course"}: ${issue.message}`);
    }
    return result.data;
}

export function courseLifecycle(course: Pick<CourseRun, "courseStatus" | "startAt" | "endAt">, now = new Date()): CourseLifecycle {
    if (course.courseStatus === "completed" || course.courseStatus === "cancelled" || course.courseStatus === "draft") return course.courseStatus;
    const time = now.getTime();
    if (time < new Date(course.startAt).getTime()) return "scheduled";
    if (time <= new Date(course.endAt).getTime()) return "active";
    return "closing";
}

export function attendancePercent(records: CourseAttendance[], sessions: CourseSession[]) {
    const attended = new Map(records.map((record) => [record.sessionId, record.status]));
    const applicable = sessions.filter((session) => session.type !== "RECORDED" && !session.cancelled)
        .map((session) => attended.get(session.id) || "NOT_MARKED")
        .filter((status) => status !== "EXCUSED" && status !== "NOT_MARKED");
    if (!applicable.length) return 100;
    return Math.round(applicable.filter((status) => status === "PRESENT").length / applicable.length * 100);
}

export function allSessions(course: Pick<CourseRun, "modules">) {
    return course.modules.flatMap((module) => module.sessions);
}

export function requiredAssignments(course: Pick<CourseRun, "modules">) {
    return course.modules.filter((module) => module.required).flatMap((module) => module.sessions.filter((session) => session.required && !session.cancelled).flatMap((session) => session.assignments
        .filter((assignment) => assignment.required)
        .map((assignment) => ({ ...assignment, moduleId: module.id, sessionId: session.id }))));
}

export function completedRequiredModules(course: Pick<CourseRun, "modules">, submissions: CourseSubmission[], now = new Date()) {
    const submitted = new Set(submissions.map((record) => record.assignmentId));
    const requiredModules = course.modules.filter((module) => module.required);
    return requiredModules.filter((module) => module.sessions.every((session) => session.cancelled || new Date(session.endsAt).getTime() <= now.getTime())
        && module.sessions.filter((session) => session.required && !session.cancelled)
            .flatMap((session) => session.assignments)
            .filter((assignment) => assignment.required)
            .every((assignment) => submitted.has(assignment.id))).length;
}

export function courseProgressPercent(course: Pick<CourseRun, "courseStatus" | "modules">, submissions: CourseSubmission[], now = new Date()) {
    if (course.courseStatus === "completed") return 100;
    const requiredModules = course.modules.filter((module) => module.required);
    if (!requiredModules.length) return 0;
    return Math.round(completedRequiredModules(course, submissions, now) / requiredModules.length * 100);
}

export type CourseNextAction = { kind: "assignment" | "session"; title: string; at?: string; moduleLabel?: string };

export function nextCourseAction(course: Pick<CourseRun, "courseStatus" | "modules">, submissions: CourseSubmission[], now = new Date()): CourseNextAction | undefined {
    if (course.courseStatus !== "published") return undefined;
    const submitted = new Set(submissions.map((record) => record.assignmentId));
    for (const module of course.modules) {
        for (const session of module.sessions) {
            if (session.cancelled || new Date(session.endsAt).getTime() > now.getTime()) continue;
            const assignment = session.assignments.find((item) => item.required && !submitted.has(item.id));
            if (assignment) return { kind: "assignment", title: assignment.title, moduleLabel: module.label || module.title };
        }
    }
    const upcoming = course.modules.flatMap((module) => module.sessions
        .filter((session) => !session.cancelled && new Date(session.startsAt).getTime() >= now.getTime())
        .map((session) => ({ session, module })))
        .sort((a, b) => a.session.startsAt.localeCompare(b.session.startsAt))[0];
    return upcoming ? { kind: "session", title: upcoming.session.title, at: upcoming.session.startsAt, moduleLabel: upcoming.module.label || upcoming.module.title } : undefined;
}

export const SAMPLE_COURSE_JSON = JSON.stringify({
    schemaVersion: 1,
    courseTitle: "Full Stack Development",
    courseType: "INTERNSHIP",
    programTrackKey: "web-development",
    programTrackLabel: "Web Development",
    programTier: 1,
    description: "A practical full-stack program built around live instruction and hands-on assignments.",
    learningOutcomes: ["Build and deploy a production-ready React application"],
    imageUrl: "",
    coverImageUrl: "",
    cardImageUrl: "",
    runTitle: "October 2026 Cohort",
    startAt: "2026-10-01T09:00:00+05:30",
    endAt: "2026-11-01T18:00:00+05:30",
    timezone: "Asia/Kolkata",
    minimumAttendancePercent: 75,
    enrollmentMode: "INVITATION",
    publicationStatus: "draft",
    modules: [{
        id: "module-1", title: "React fundamentals", label: "Week 1", required: true,
        sessions: [{
            id: "session-1", title: "Components and props", type: "LIVE",
            startsAt: "2026-10-01T10:00:00+05:30", endsAt: "2026-10-01T11:30:00+05:30",
            joinUrl: "", recordingUrl: "", venue: "", required: true,
            cancelled: false,
            assignments: [{ id: "assignment-1", title: "Component exercise", description: "Share an HTTPS link to a ZIP file.", required: true }],
        }],
    }],
}, null, 2);
